import { z } from 'zod';
import { EMAIL_INVALID_MESSAGE } from './email-input.js';
import { singleLineText } from './text-input.js';
import { EMAIL_TOO_LONG_MESSAGE, MAX_EMAIL_LENGTH, MAX_NOTE_LENGTH, MAX_PERSON_NAME_LENGTH, MAX_PHONE_LENGTH, MAX_SHORT_TEXT_LENGTH, } from './validation.js';
// Kontaktní formulář landing page „Napište nám“ / „Požádat o demo“ (FR02 20261001_LandingPage/
// 002_Marketing, FR-01). Schéma sdílí formulář v apps/landing i veřejný endpoint POST /api/contact.
// Zpráva jde jen provozovateli Trenigo (adresa z konfigurace), adresa návštěvníka slouží jako Reply-To.
export const CONTACT_FORM_TYPES = ['CONTACT', 'DEMO'];
export const CONTACT_FORM_TYPE_LABELS = {
    CONTACT: 'Napište nám',
    DEMO: 'Požádat o demo',
};
// Zdroj poptávky „Jak jste se o nás dozvěděli?“ (FR01 20261022_ContactReferral/001_Marketing, FR-01).
// Pořadí je pořadí voleb ve formuláři; popisky jsou jediný zdroj textu pro formulář i e-mail.
export const CONTACT_REFERRAL_SOURCES = ['RECOMMENDATION', 'SEARCH', 'SOCIAL', 'EVENT', 'OTHER'];
export const CONTACT_REFERRAL_SOURCE_LABELS = {
    RECOMMENDATION: 'Doporučení',
    SEARCH: 'Internetový vyhledávač',
    SOCIAL: 'Sociální sítě',
    EVENT: 'Akce nebo konference',
    OTHER: 'Jinak',
};
export const CONTACT_REFERRAL_SOURCE_INVALID_MESSAGE = 'Vyberte jednu z nabízených možností.';
export const CONTACT_FORM_NAME_MESSAGE = 'Vyplňte jméno.';
export const CONTACT_FORM_EMAIL_MESSAGE = 'Vyplňte e-mail.';
export const CONTACT_FORM_MESSAGE_REQUIRED_MESSAGE = 'Napište prosím zprávu.';
// Jednořádková pole (jméno jde do předmětu e-mailu) používají sdílený vzor `singleLineText`
// (text-input.ts) - odmítá konce řádků a řídicí znaky.
// Znaky, které e-mailová syntaxe senderu odmítá (apps/functions/src/lib/email/email-address.ts), ale
// Zod `.email()` některé z nich propustí (např. apostrof). Adresa návštěvníka jde do Reply-To,
// takže ji musí přijmout i sender - jinak by enqueue skončil chybou serveru místo chyby pole.
const EMAIL_HEADER_UNSAFE_CHARS = [',', ';', ':', '<', '>', '"', "'", '\\', ' '];
/** Prázdný nebo jen mezerový řetězec volitelného pole -> `undefined` (vzor `guardian.ts`). */
function blankToUndefined(value) {
    return typeof value === 'string' && value.trim() === '' ? undefined : value;
}
const zodEmail = z.string().email();
/** Jediná kontrola syntaxe (Zod `.email()` a znaky nepřípustné v hlavičce), aby pole mělo jednu hlášku. */
function isAcceptableEmail(value) {
    return zodEmail.safeParse(value).success && !EMAIL_HEADER_UNSAFE_CHARS.some((char) => value.includes(char));
}
// Společná pole obou typů zprávy. Typ je diskriminátor sjednocení níže, takže povinnost zprávy se
// vyhodnotí v jednom průchodu s ostatními poli. S `superRefine` nad objektem by se chyba zprávy
// ukázala až po opravě všech ostatních polí.
const contactFormBaseShape = {
    name: singleLineText(CONTACT_FORM_NAME_MESSAGE, MAX_PERSON_NAME_LENGTH, 'Jméno je příliš dlouhé.'),
    email: z
        .string({ required_error: CONTACT_FORM_EMAIL_MESSAGE, invalid_type_error: CONTACT_FORM_EMAIL_MESSAGE })
        .trim()
        .min(1, CONTACT_FORM_EMAIL_MESSAGE)
        .max(MAX_EMAIL_LENGTH, EMAIL_TOO_LONG_MESSAGE)
        .refine(isAcceptableEmail, EMAIL_INVALID_MESSAGE),
    phone: z.preprocess(blankToUndefined, singleLineText('Vyplňte telefon.', MAX_PHONE_LENGTH, 'Telefon je příliš dlouhý.').optional()),
    organization: z.preprocess(blankToUndefined, singleLineText('Vyplňte název akademie nebo oddílu.', MAX_SHORT_TEXT_LENGTH, 'Název je příliš dlouhý.').optional()),
    // Zdroj poptávky a doporučitel (FR01 20261022_ContactReferral, FR-02): obě pole nepovinná a na sobě
    // nezávislá; nevybraná položka výběru (prázdný řetězec) znamená „nevyplněno“.
    referralSource: z.preprocess(blankToUndefined, z
        .enum(CONTACT_REFERRAL_SOURCES, {
        errorMap: () => ({ message: CONTACT_REFERRAL_SOURCE_INVALID_MESSAGE }),
    })
        .optional()),
    referredBy: z.preprocess(blankToUndefined, singleLineText('Vyplňte, kdo nás doporučil.', MAX_SHORT_TEXT_LENGTH, 'Text je příliš dlouhý.').optional()),
    // Honeypot: skryté pole, které člověk nevyplní. Schéma ho záměrně přijme vyplněné - endpoint
    // takový požadavek tiše zahodí s odpovědí 202 (FR-06), aby robot z odpovědi nepoznal odmítnutí.
    // Rozhoduje `isContactHoneypotFilled`.
    website: z.string().max(MAX_SHORT_TEXT_LENGTH).optional(),
    // Generuje klient při zobrazení formuláře; opakované odeslání téhož formuláře (dvojklik, retry
    // sítě) tak vytvoří jen jednu zprávu (idempotencyKey `contact:<submissionId>`, FR-05).
    submissionId: z
        .string({ required_error: 'Chybí identifikátor formuláře.' })
        .uuid('Neplatný identifikátor formuláře.'),
};
/** Zpráva: prázdná nebo jen mezerová hodnota znamená „nevyplněno“, délka se měří nad oříznutou. */
function messageField(required) {
    const text = z
        .string({ required_error: CONTACT_FORM_MESSAGE_REQUIRED_MESSAGE, invalid_type_error: CONTACT_FORM_MESSAGE_REQUIRED_MESSAGE })
        .transform((value) => value.trim())
        .pipe(z.string().max(MAX_NOTE_LENGTH, 'Zpráva je příliš dlouhá.'));
    return z.preprocess(blankToUndefined, required ? text : text.optional());
}
const UNKNOWN_FIELD_MESSAGE = 'Formulář obsahuje neznámé pole.';
export const contactFormSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('CONTACT'), message: messageField(true), ...contactFormBaseShape }).strict(UNKNOWN_FIELD_MESSAGE),
    z.object({ type: z.literal('DEMO'), message: messageField(false), ...contactFormBaseShape }).strict(UNKNOWN_FIELD_MESSAGE),
], {
    errorMap: (issue, ctx) => issue.code === z.ZodIssueCode.invalid_union_discriminator ? { message: 'Vyberte typ zprávy.' } : { message: ctx.defaultError },
});
/**
 * Vyplněný honeypot (FR-06). Čte se z nevalidovaného těla, aby robot dostal stejnou odpověď 202
 * i s jinak neplatným obsahem formuláře.
 */
export function isContactHoneypotFilled(body) {
    if (typeof body !== 'object' || body === null)
        return false;
    const website = body.website;
    return typeof website === 'string' ? website.trim() !== '' : website !== undefined && website !== null;
}
